/**
 * Workflow Registry Hook
 *
 * Provides data-driven workflow configuration from the backend.
 * NO HARDCODED WORKFLOW DEFINITIONS - everything comes from the registry.
 *
 * This enables:
 * - Adding/modifying workflows without code changes
 * - Tenant-specific workflow customization
 * - MN DHS program variant configurations
 * - Dynamic step definitions that can change over time
 *
 * @example
 * ```tsx
 * const { workflows, getWorkflow, loading } = useWorkflowRegistry({
 *   programVariant: 'ELDERLY_WAIVER',
 * });
 *
 * // Workflows come from backend, not hardcoded
 * workflows.forEach(wf => console.log(wf.name, wf.steps.length));
 * ```
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';

// ============================================================================
// GraphQL Queries - Fetch workflow definitions from backend registry
// ============================================================================

const GET_WORKFLOW_REGISTRY = gql`
  query GetWorkflowRegistry($tenantId: String!, $programVariant: String) {
    workflowRegistry(tenantId: $tenantId, programVariant: $programVariant) {
      workflows {
        id
        workflowName
        workflowVersion
        displayName
        description
        icon
        color
        criticality
        estimatedSteps
        complianceDomains
        executionMode
        failureMode
        maxRetries
        parallelGroup
        repeating
        repeatFrequency
      }
      dependencies {
        dependent
        required
        condition
      }
      programConfig {
        variant
        name
        description
        color
        serviceCodes
        supervisionRequirements {
          supervisionType
          frequency
          documentationRequired
          supervisorCredentials
        }
        eligibility {
          minimumAge
        }
        evvRequirement {
          enabled
          method
          gracePeriodMinutes
        }
        workflowOverrides
      }
    }
  }
`;

const GET_WORKFLOW_DEFINITION = gql`
  query GetWorkflowDefinition(
    $tenantId: String!
    $workflowName: String!
    $version: String
    $programVariant: String
  ) {
    workflowDefinition(
      tenantId: $tenantId
      workflowName: $workflowName
      version: $version
      programVariant: $programVariant
    ) {
      id
      name
      version
      description
      isLifeCritical
      timeoutSeconds
      nodes {
        id
        nodeType
        label
        description
        criticality
        validationRules {
          id
          name
          ruleType
          expression
          errorMessage
          severity
        }
        complianceChecks {
          id
          domain
          checkType
          required
        }
        timeoutSeconds
      }
      edges {
        id
        source
        target
        condition
      }
      metadata {
        complianceDomains
        auditLevel
        retentionDays
        category
        industry
      }
    }
  }
`;

const GET_MASTER_WORKFLOW_CONFIG = gql`
  query GetMasterWorkflowConfig($tenantId: String!, $programVariant: String!) {
    masterWorkflowConfig(tenantId: $tenantId, programVariant: $programVariant) {
      id
      name
      version
      programVariant
      subWorkflows {
        id
        workflowName
        workflowVersion
        displayName
        description
        icon
        color
        criticality
        complianceDomains
        estimatedSteps
        executionMode
        failureMode
        maxRetries
        parallelGroup
        repeating
        repeatFrequency
      }
      dependencies {
        dependent
        required
        condition
        description
      }
      phases {
        id
        name
        description
        workflows
        parallel
      }
      complianceDomains
      metadata
    }
  }
`;

// ============================================================================
// Types - All from backend, no hardcoded definitions
// ============================================================================

export type WorkflowCriticality =
  | 'Operational'
  | 'ComplianceCritical'
  | 'FinanciallySignificant'
  | 'PatientImpact'
  | 'LifeCritical';

export type ExecutionMode = 'Inline' | 'Async' | 'Parallel';

export type FailureMode = 'FailParent' | 'SkipAndContinue' | 'RetryChild' | 'Compensate';

export interface WorkflowRegistryEntry {
  id: string;
  workflowName: string;
  workflowVersion: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  criticality: WorkflowCriticality;
  estimatedSteps: number;
  complianceDomains: string[];
  executionMode: ExecutionMode;
  failureMode: FailureMode;
  maxRetries: number;
  parallelGroup?: string;
  repeating: boolean;
  repeatFrequency?: string;
}

export interface WorkflowDependency {
  dependent: string;
  required: string;
  condition?: string;
  description?: string;
}

export interface SupervisionConfig {
  supervisionType?: string;
  frequency?: string;
  documentationRequired: boolean;
  supervisorCredentials: string[];
}

export interface EligibilityConfig {
  minimumAge?: number;
  nursingFacilityLevel?: string;
  financialEligibility?: string;
  developmentalDisability?: string;
}

export interface EvvConfig {
  enabled: boolean;
  method?: string;
  gracePeriodMinutes?: number;
}

export interface ProgramConfig {
  variant: string;
  name: string;
  description?: string;
  color?: string;
  serviceCodes: string[];
  supervisionRequirements?: SupervisionConfig;
  eligibility?: EligibilityConfig;
  evvRequirement?: EvvConfig;
  workflowOverrides?: unknown;
}

export interface WorkflowNode {
  id: string;
  nodeType: string;
  label: string;
  description?: string;
  criticality: WorkflowCriticality;
  validationRules: ValidationRule[];
  complianceChecks: ComplianceCheckDef[];
  timeoutSeconds?: number;
}

export interface ValidationRule {
  id: string;
  name: string;
  ruleType: string;
  expression?: string;
  errorMessage: string;
  severity: string;
}

export interface ComplianceCheckDef {
  id: string;
  domain: string;
  checkType: string;
  required: boolean;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  condition?: string;
}

export interface WorkflowMetadata {
  complianceDomains: string[];
  auditLevel?: string;
  retentionDays?: number;
  category?: string;
  industry?: string;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  version: string;
  description?: string;
  isLifeCritical: boolean;
  timeoutSeconds?: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  metadata?: WorkflowMetadata;
}

export interface SubWorkflowConfig {
  id: string;
  workflowName: string;
  workflowVersion: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  criticality: WorkflowCriticality;
  complianceDomains: string[];
  estimatedSteps: number;
  executionMode: ExecutionMode;
  failureMode: FailureMode;
  maxRetries: number;
  parallelGroup?: string;
  repeating: boolean;
  repeatFrequency?: string;
}

export interface WorkflowPhase {
  id: string;
  name: string;
  description?: string;
  workflows: string[];
  parallel: boolean;
}

export interface MasterWorkflowConfig {
  id: string;
  name: string;
  version: string;
  programVariant: string;
  subWorkflows: SubWorkflowConfig[];
  dependencies: WorkflowDependency[];
  phases: WorkflowPhase[];
  complianceDomains: string[];
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Hook Options
// ============================================================================

export interface UseWorkflowRegistryOptions {
  tenantId?: string;
  programVariant?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface UseWorkflowDefinitionOptions {
  tenantId?: string;
  workflowName: string;
  version?: string;
  programVariant?: string;
}

export interface UseMasterWorkflowConfigOptions {
  tenantId?: string;
  programVariant: string;
}

// ============================================================================
// Hook Results
// ============================================================================

export interface UseWorkflowRegistryResult {
  workflows: WorkflowRegistryEntry[];
  dependencies: WorkflowDependency[];
  programConfig?: ProgramConfig;
  loading: boolean;
  error?: Error;
  refetch: () => void;
  getWorkflow: (id: string) => WorkflowRegistryEntry | undefined;
  getWorkflowsByDomain: (domain: string) => WorkflowRegistryEntry[];
}

export interface UseWorkflowDefinitionResult {
  definition?: WorkflowDefinition;
  loading: boolean;
  error?: Error;
  refetch: () => void;
  getNode: (nodeId: string) => WorkflowNode | undefined;
  getNodesByType: (nodeType: string) => WorkflowNode[];
  getComplianceChecks: () => ComplianceCheckDef[];
}

export interface UseMasterWorkflowConfigResult {
  config?: MasterWorkflowConfig;
  loading: boolean;
  error?: Error;
  refetch: () => void;
  getSubWorkflow: (workflowName: string) => SubWorkflowConfig | undefined;
  getPhase: (phaseId: string) => WorkflowPhase | undefined;
  getDependenciesFor: (workflowName: string) => WorkflowDependency[];
  getReadyWorkflows: (completedWorkflows: string[]) => SubWorkflowConfig[];
}

// ============================================================================
// Hooks Implementation
// ============================================================================

/**
 * Hook to fetch workflow registry from backend
 * NO HARDCODED WORKFLOWS - all data comes from the registry
 */
export function useWorkflowRegistry(
  options: UseWorkflowRegistryOptions = {}
): UseWorkflowRegistryResult {
  const { tenantId = 'default', programVariant, autoRefresh = false, refreshInterval = 30000 } = options;

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_WORKFLOW_REGISTRY, {
    variables: { tenantId, programVariant },
    pollInterval: autoRefresh ? refreshInterval : 0,
  });

  const workflows: WorkflowRegistryEntry[] = data?.workflowRegistry?.workflows ?? [];
  const dependencies: WorkflowDependency[] = data?.workflowRegistry?.dependencies ?? [];
  const programConfig: ProgramConfig | undefined = data?.workflowRegistry?.programConfig;

  const getWorkflow = useMemo(() => {
    const workflowMap = new Map(workflows.map((w) => [w.id, w]));
    return (id: string) => workflowMap.get(id);
  }, [workflows]);

  const getWorkflowsByDomain = useMemo(() => {
    return (domain: string) =>
      workflows.filter((w) => w.complianceDomains.includes(domain));
  }, [workflows]);

  return {
    workflows,
    dependencies,
    programConfig,
    loading,
    error: error as Error | undefined,
    refetch,
    getWorkflow,
    getWorkflowsByDomain,
  };
}

/**
 * Hook to fetch a specific workflow definition with all its nodes and edges
 * Steps, validations, and compliance checks all come from backend
 */
export function useWorkflowDefinition(
  options: UseWorkflowDefinitionOptions
): UseWorkflowDefinitionResult {
  const { tenantId = 'default', workflowName, version, programVariant } = options;

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_WORKFLOW_DEFINITION, {
    variables: { tenantId, workflowName, version, programVariant },
    skip: !workflowName,
  });

  const definition: WorkflowDefinition | undefined = data?.workflowDefinition;

  const getNode = useMemo(() => {
    if (!definition) return () => undefined;
    const nodeMap = new Map(definition.nodes.map((n) => [n.id, n]));
    return (nodeId: string) => nodeMap.get(nodeId);
  }, [definition]);

  const getNodesByType = useMemo(() => {
    if (!definition) return () => [];
    return (nodeType: string) =>
      definition.nodes.filter((n) => n.nodeType === nodeType);
  }, [definition]);

  const getComplianceChecks = useMemo(() => {
    if (!definition) return () => [];
    return () => definition.nodes.flatMap((n) => n.complianceChecks);
  }, [definition]);

  return {
    definition,
    loading,
    error: error as Error | undefined,
    refetch,
    getNode,
    getNodesByType,
    getComplianceChecks,
  };
}

/**
 * Hook to fetch master workflow configuration for a specific program variant
 * Sub-workflow list, dependencies, and phases all from backend
 */
export function useMasterWorkflowConfig(
  options: UseMasterWorkflowConfigOptions
): UseMasterWorkflowConfigResult {
  const { tenantId = 'default', programVariant } = options;

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_MASTER_WORKFLOW_CONFIG, {
    variables: { tenantId, programVariant },
    skip: !programVariant,
  });

  const config: MasterWorkflowConfig | undefined = data?.masterWorkflowConfig;

  const getSubWorkflow = useMemo(() => {
    if (!config) return () => undefined;
    const subWorkflowMap = new Map(config.subWorkflows.map((sw) => [sw.workflowName, sw]));
    return (workflowName: string) => subWorkflowMap.get(workflowName);
  }, [config]);

  const getPhase = useMemo(() => {
    if (!config) return () => undefined;
    const phaseMap = new Map(config.phases.map((p) => [p.id, p]));
    return (phaseId: string) => phaseMap.get(phaseId);
  }, [config]);

  const getDependenciesFor = useMemo(() => {
    if (!config) return () => [];
    return (workflowName: string) =>
      config.dependencies.filter((d) => d.dependent === workflowName);
  }, [config]);

  const getReadyWorkflows = useMemo(() => {
    if (!config) return () => [];
    return (completedWorkflows: string[]) => {
      const completedSet = new Set(completedWorkflows);
      return config.subWorkflows.filter((sw) => {
        const deps = config.dependencies.filter((d) => d.dependent === sw.workflowName);
        return deps.every((d) => completedSet.has(d.required));
      });
    };
  }, [config]);

  return {
    config,
    loading,
    error: error as Error | undefined,
    refetch,
    getSubWorkflow,
    getPhase,
    getDependenciesFor,
    getReadyWorkflows,
  };
}

// ============================================================================
// Program Variants Hook
// ============================================================================

export interface UseProgramVariantsResult {
  variants: ProgramConfig[];
  loading: boolean;
  error?: Error;
  refetch: () => void;
}

/**
 * Hook to fetch available program variants from backend
 */
export function useProgramVariants(tenantId: string = 'default'): UseProgramVariantsResult {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_WORKFLOW_REGISTRY, {
    variables: { tenantId },
  });

  const variants: ProgramConfig[] = data?.workflowRegistry?.programConfig
    ? [data?.workflowRegistry.programConfig]
    : [];

  return {
    variants,
    loading,
    error: error as Error | undefined,
    refetch,
  };
}

// ============================================================================
// Ready Workflows Hook
// ============================================================================

export interface UseReadyWorkflowsOptions {
  tenantId?: string;
  programVariant: string;
  completedWorkflows: string[];
}

export interface UseReadyWorkflowsResult {
  readyWorkflows: SubWorkflowConfig[];
  loading: boolean;
  error?: Error;
}

/**
 * Hook to get workflows that are ready to execute based on completed dependencies
 */
export function useReadyWorkflows(options: UseReadyWorkflowsOptions): UseReadyWorkflowsResult {
  const { tenantId = 'default', programVariant, completedWorkflows } = options;

  const { config, loading, error } = useMasterWorkflowConfig({ tenantId, programVariant });

  const readyWorkflows = useMemo(() => {
    if (!config) return [];
    const completedSet = new Set(completedWorkflows);
    return config.subWorkflows.filter((sw) => {
      const deps = config.dependencies.filter((d) => d.dependent === sw.workflowName);
      return deps.every((d) => completedSet.has(d.required));
    });
  }, [config, completedWorkflows]);

  return {
    readyWorkflows,
    loading,
    error,
  };
}

// ============================================================================
// Reload Workflow Configs Hook
// ============================================================================

export interface UseReloadWorkflowConfigsResult {
  reload: () => Promise<void>;
  reloading: boolean;
}

/**
 * Hook to trigger a reload of workflow configs from backend
 */
export function useReloadWorkflowConfigs(): UseReloadWorkflowConfigsResult {
  const [reloading, setReloading] = useState(false);

  const reload = async () => {
    setReloading(true);
    // In the future, this could call a mutation to refresh server-side cache
    await new Promise(resolve => setTimeout(resolve, 500));
    setReloading(false);
  };

  return {
    reload,
    reloading,
  };
}

// ============================================================================
// Fallback for development - loads from local JSON when backend unavailable
// ============================================================================

/**
 * Development fallback - loads workflow config from local JSON files
 * ONLY FOR DEVELOPMENT - production should always use backend registry
 */
export async function loadLocalWorkflowConfig(
  basePath: string,
  programVariant: string
): Promise<MasterWorkflowConfig | null> {
  try {
    const response = await fetch(`${basePath}/master_business_operations_dag.json`);
    if (!response.ok) return null;

    const masterDag = await response.json();

    // Transform JSON to MasterWorkflowConfig format
    return {
      id: masterDag.id,
      name: masterDag.name,
      version: masterDag.version,
      programVariant,
      subWorkflows: masterDag.nodes
        .filter((n: Record<string, unknown>) => n.node_type === 'SubWorkflow')
        .map((n: Record<string, unknown>) => ({
          id: n.id as string,
          workflowName: (n.metadata as Record<string, unknown>)?.workflow_name as string ?? '',
          workflowVersion: (n.metadata as Record<string, unknown>)?.workflow_version as string ?? '1.0.0',
          displayName: n.label as string,
          description: (n.metadata as Record<string, unknown>)?.description as string,
          criticality: n.criticality as WorkflowCriticality,
          complianceDomains: [],
          estimatedSteps: 8,
          executionMode: ((n.metadata as Record<string, unknown>)?.execution_mode as ExecutionMode) ?? 'Async',
          failureMode: ((n.metadata as Record<string, unknown>)?.failure_mode as FailureMode) ?? 'RetryChild',
          maxRetries: ((n.metadata as Record<string, unknown>)?.max_retries as number) ?? 3,
          repeating: ((n.metadata as Record<string, unknown>)?.repeating as boolean) ?? false,
          repeatFrequency: (n.metadata as Record<string, unknown>)?.repeat_frequency as string,
        })),
      dependencies: masterDag.edges
        .filter((e: Record<string, unknown>) => e.condition)
        .map((e: Record<string, unknown>) => ({
          dependent: e.target as string,
          required: e.source as string,
          condition: e.condition as string,
          description: (e.metadata as Record<string, unknown>)?.comment as string,
        })),
      phases: [],
      complianceDomains: masterDag.metadata?.compliance_domains ?? [],
      metadata: masterDag.metadata,
    };
  } catch (error) {
    console.error('Failed to load local workflow config:', error);
    return null;
  }
}
